<template>
    <div class="relative sm:space-y-6 sm:px-4 lg:px-8 min-h-[calc(100vh-8rem)] flex flex-col">
        <!-- Breadcrumbs -->
        <nav class="sm:pt-6 pt-4 sm:px-0 text-sm text-slate-500 dark:text-slate-400">
            <a href="#" @click.prevent="confirmLeave" class="hover:text-primary transition-colors">
                Materials
            </a>
            <ChevronRight class="mx-1 h-3.5 w-3.5 inline-block align-middle text-slate-400" />
            <span class="text-slate-800 dark:text-slate-200 font-medium">{{ material?.title || 'Online Form' }}</span>
        </nav>

        <!-- Loading -->
        <div v-if="loading" class="flex items-center justify-center py-20">
            <Loader2 class="w-8 h-8 animate-spin text-primary" />
        </div>

        <!-- Error -->
        <div v-else-if="error" class="bg-red-50 text-red-600 p-4 rounded-xl border border-red-200 sm:mx-0 mx-3">
            {{ error }}
        </div>

        <!-- Embedded Form -->
        <div v-else-if="material" class="flex-1 flex flex-col min-h-[90vh] sm:bg-white dark:bg-slate-800/80 sm:rounded-2xl sm:shadow-lg sm:border sm:border-slate-200/50 dark:border-slate-700/50 sm:overflow-hidden">
            <div class="flex items-center justify-between sm:px-6 py-3 border-b border-slate-200 dark:border-slate-700">
                <h1 class="text-lg font-semibold text-slate-800 dark:text-slate-200 truncate">{{ material.title }}</h1>
                <button @click="confirmLeave"
                    class="flex items-center px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0 ml-4">
                    <ChevronLeft class="w-5 h-5 text-primary" /> Back to Materials
                </button>
            </div>
            <div class="sm:flex-1 sm:relative sm:min-h-0">
                <div v-if="iframeLoading" class="absolute inset-0 flex items-center justify-center bg-white dark:bg-slate-800/80 z-10">
                    <div class="text-center">
                        <div class="w-8 h-8 rounded-full border-4 border-gray-200 border-t-primary animate-spin mx-auto"></div>
                        <p class="text-sm text-slate-500 dark:text-slate-400 mt-3">Loading form...</p>
                    </div>
                </div>
                <iframe :src="iframeSrc" class="w-full sm:absolute sm:inset-0 sm:w-full sm:h-full min-h-[80vh] sm:min-h-0" frameborder="0" allowfullscreen @load="iframeLoading = false" />
            </div>
        </div>

        <!-- Leave Confirmation Modal -->
        <Modal v-model="showLeaveConfirm" title="Leave Online Form?">
            <p class="text-sm text-secondary-text/80 dark:text-secondary-dark-text/80">
                Are you sure you want to go back? Any unsaved changes will not be saved.
            </p>
            <template #footer>
                <button type="button"
                    class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors duration-200"
                    @click="showLeaveConfirm = false">
                    Cancel
                </button>
                <button type="button"
                    class="flex justify-center ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-success hover:bg-success/90 shadow-sm hover:shadow-md transition-all duration-200"
                    @click="leave">
                    Yes, go back
                </button>
            </template>
        </Modal>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-vue-next'
import { materialService } from '@/services/material.service'
import type { Material } from '@/config/types/material'
import Modal from '@/components/layout/Modal.vue'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const material = ref<Material | null>(null)
const loading = ref(true)
const error = ref('')
const showLeaveConfirm = ref(false)
const iframeLoading = ref(true)

const backLink = computed(() =>
    route.name === 'employee-view-online-form' ? '/employee-materials' : '/admin/materials'
)

const iframeSrc = computed(() => {
    if (!material.value?.url_link) return ''
    const url = new URL(material.value.url_link)
    url.searchParams.set('material_id', String(material.value.id))
    url.searchParams.set('source', 'planet_home_sp')
    url.searchParams.set('user_id', String(authStore.currentUser?.id || ''))
    return url.toString()
})

const confirmLeave = () => {
    showLeaveConfirm.value = true
}

const leave = () => {
    showLeaveConfirm.value = false
    router.push(backLink.value)
}

const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault()
    e.returnValue = ''
}

onMounted(async () => {
    try {
        const id = Number(route.params.id)
        material.value = await materialService.fetchById(id)
        window.addEventListener('beforeunload', handleBeforeUnload)
    } catch (err: any) {
        error.value = err.message || 'Failed to load online form'
    } finally {
        loading.value = false
    }
})

onUnmounted(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
})
</script>
